'use client';

import { useId } from 'react';

export interface RingGaugeProps {
	/** Percentuale mostrata al centro dell'anello (0-100), es. quota di autoconsumo. */
	percentage: number;
	/** Diametro dell'anello SVG in pixel. Default: 120. */
	size?: number;
	/**
	 * Cosa misura l'anello: va nel nome accessibile e, se `caption` non è
	 * disattivata, sotto la percentuale. Default: `autoconsumo`.
	 */
	label?: string;
	/**
	 * Didascalia sotto la percentuale. `false` la toglie e lascia il solo numero
	 * al centro, come nel mock v11, dove il titolo della card dice già cos'è.
	 */
	caption?: boolean;
}

/** Indicatore circolare a ciambella per una percentuale singola (tipicamente la quota di autoconsumo energetico). */
export function RingGauge({
	percentage,
	size = 120,
	label = 'autoconsumo',
	caption = true,
}: RingGaugeProps) {
	const gradientId = useId();
	const radius = (size - 12) / 2;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (Math.max(0, Math.min(100, percentage)) / 100) * circumference;
	const accessibleName = `${label.charAt(0).toUpperCase()}${label.slice(1)} ${percentage}%`;
	// Spessore e corpo della percentuale seguono il diametro come nel mock, dove
	// l'anello è disegnato in un viewBox da 120 e poi scalato: a 120px i valori
	// restano 10 e 20, a 168px crescono con il cerchio invece di assottigliarsi.
	const strokeWidth = (size / 120) * 10;
	const valueFontSize = Math.round((size / 120) * 20);

	return (
		<div className="flex justify-center">
			<svg
				width={size}
				height={size}
				className="-rotate-90"
				// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- inline SVG: role="img" + aria-label + <title> is the standard accessible-SVG pattern; no <img> tag equivalent
				role="img"
				aria-label={accessibleName}
			>
				<title>{accessibleName}</title>
				<defs>
					{/* Il gradiente del mock: si parte dall'accento di marca e si arriva al
					    verde, così l'anello dice «più è pieno, meglio è» anche senza leggere
					    il numero. Colore fisso (`--color-series-*`), non dal tema: il
					    mock scrive l'anello come `#00AEEF`→`#10b981` letterali — stessa
					    ragione dei colori di serie nei grafici, vedi tokens.css. Con
					    `--color-accent` l'anello si scuriva in tema chiaro invece di
					    restare ciano→verde come nel mock. */}
					<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
						<stop offset="0%" stopColor="var(--color-series-1)" />
						<stop offset="100%" stopColor="var(--color-series-4)" />
					</linearGradient>
				</defs>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					// Il solco è il bordo all'80%, non pieno: sul fondo della card il
					// navy pieno stacca troppo e l'anello sembra a due colori.
					stroke="color-mix(in srgb, var(--color-border) 80%, transparent)"
					strokeWidth={strokeWidth}
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke={`url(#${gradientId})`}
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					className="transition-all duration-700"
				/>
			</svg>
			<div
				className="absolute flex flex-col items-center justify-center"
				style={{ width: size, height: size }}
			>
				<span
					className="font-mono font-extrabold leading-none text-accent"
					style={{ fontSize: valueFontSize }}
				>
					{percentage}%
				</span>
				{caption && <span className="text-[11px] text-text-muted">{label}</span>}
			</div>
		</div>
	);
}
